import AssetLoader from "./asset-loader";

export default class FigureThumbnail {
  assetLoader: AssetLoader;

  constructor() {
    this.assetLoader = new AssetLoader();
  }

  async createThumbnail(
    spriteName: string,
    x: number,
    y: number,
    animationName: string = "idle_down",
    tintColor: string = "rgba(66, 135, 245, 0.5)",
    useAbsolutePosition: boolean = false,
    anchor: { x: number; y: number } = { x: 0.5, y: 0.5 } // Default anchor is center (0.5, 0.5)
  ) {
    const asset = this.assetLoader.getAssetById(spriteName);
    if (asset) {
      let animation = asset.resource.getAnimation(animationName);

      const sprite = animation?.currentFrame?.graphic;

      if (sprite) {
        const sourceView = sprite.sourceView;
        const image = sprite.image.data;
        const { x: sourceX, y: sourceY, width, height } = sourceView;

        // Create a parent container for layout control
        const container = document.createElement("div");
        container.id = spriteName;

        // Set the container background properties to display the sprite
        Object.assign(container.style, {
          width: `${width}px`,
          height: `${height}px`,
          position: `absolute`,
          bottom: y + "px",
          left: `50%`,
          transform: `translateX(-50%)`,
          backgroundImage: `url(${image.src})`,
          backgroundPosition: `-${sourceX}px -${sourceY}px`,
          backgroundRepeat: "no-repeat",
        });

        // Create a darker tint overlay div with the custom tint color
        const tint = document.createElement("div");
        Object.assign(tint.style, {
          width: `${width}px`,
          height: `${height}px`,
          backgroundColor: tintColor,
          maskImage: `url(${image.src})`,
          maskPosition: `-${sourceX}px -${sourceY}px`,
        });

        // Append the tint overlay to the container
        container.appendChild(tint);

        return container;
      }
    }
  }

  async createMultipleThumbnails(
    partsWithDetails: Record<
      string,
      {
        x: number;
        y: number;
        animationName: string;
        color: string;
        anchor?: { x: number; y: number };
      }
    >,
    useAbsolutePosition: boolean = true
  ) {
    // Create a parent container for all thumbnails
    const parentContainer = document.createElement("div");
    parentContainer.classList.add("thumbnails-container");

    const elements = await Promise.all(
      Object.entries(partsWithDetails).map(
        async ([spriteName, { x, y, animationName, color, anchor }]) =>
          await this.createThumbnail(
            spriteName,
            x,
            y,
            animationName,
            color,
            useAbsolutePosition,
            anchor ?? { x: 0.5, y: 0.5 } // Default anchor to center
          )
      )
    );

    // Append each thumbnail to the parent container
    elements.forEach((el) => {
      if (el) parentContainer.appendChild(el);
    });

    return parentContainer;
  }

  async replaceThumbnailSprite(
    avatar: any,
    partName: string,
    newSpriteName: string,
    animation: string
  ) {
    if (!avatar) return;

    // Find and remove the old sprite
    const oldPart = avatar.querySelector(`#${partName}`);
    if (oldPart) {
      oldPart.remove();
    }

    let bottomValue = parseFloat(oldPart.style.bottom);

    // Load the new sprite and create a new thumbnail
    const newThumbnail = await this.createThumbnail(newSpriteName, 0, bottomValue, animation); // Adjust x, y as needed
    if (newThumbnail) {
      newThumbnail.id = newSpriteName; // Set new ID
      avatar.appendChild(newThumbnail); // Add new sprite to the parent container
    }
  }

  async updateThumbnailSprite(
    avatar: any,
    partName: string,
    newSpriteName: string
  ) {
    if (!avatar) return;

    const part = avatar.querySelector(`#${partName}`);
    if (!part) return;

    // Load the new sprite
    const asset = this.assetLoader.getAssetById(newSpriteName);
    if (asset) {
      const sprite = asset.resource.getAnimation("down")?.currentFrame?.graphic; // Default to "down" animation
      if (sprite) {
        const image = sprite.image.data;
        const { x: sourceX, y: sourceY, width, height } = sprite.sourceView;

        // Update background image and position
        Object.assign(part.style, {
          width: `${width}px`,
          height: `${height}px`,
          backgroundImage: `url(${image.src})`,
          backgroundPosition: `-${sourceX}px -${sourceY}px`,
        });

        // Update the ID to match the new sprite name
        part.id = newSpriteName;
      }
    }
  }

  updateThumbnailPart(
    avatar: any,
    partName: string,
    property: "x" | "y" | "animationName" | "color",
    value: any
  ) {
    if (!avatar) return;

    const part = avatar.querySelector(`#${partName}`);
    if (!part) return;

    switch (property) {
      case "x":
        part.style.left = `${value}px`;
        break;
      case "y":
        part.style.bottom = `${value}px`;
        break;
      case "color":
        const tintDiv = part.querySelector("div"); // Assuming the tint div is inside the container
        if (tintDiv) {
          tintDiv.style.backgroundColor = value;
        }
        break;
      case "animationName":
        // Assuming animation change means switching background position
        const asset = this.assetLoader.getAssetById(partName);
        if (asset) {
          const sprite =
            asset.resource.getAnimation(value)?.currentFrame?.graphic;
          if (sprite) {
            const { x: sourceX, y: sourceY } = sprite.sourceView;
            part.style.backgroundPosition = `-${sourceX}px -${sourceY}px`;
          }
        }
        break;
    }
  }
}
